/**
 * Promise utilities. `core/firebase.js` already has its own `withTimeout`;
 * this is the generic version for non-Firebase callers (CKAN, SMS, etc).
 */

/**
 * Race a promise against a timeout. Rejects with an Error if `ms` elapses
 * before the promise settles. The underlying promise is NOT aborted — pair
 * with AbortController when the work supports cancellation.
 *
 * @template T
 * @param {Promise<T>} promise
 * @param {number} ms
 * @param {string} [label='operation']
 * @returns {Promise<T>}
 */
export function withTimeout(promise, ms, label = 'operation') {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error(`Timeout (${ms}ms): ${label}`)),
      ms,
    );
    promise.then(
      (v) => { clearTimeout(timer); resolve(v); },
      (err) => { clearTimeout(timer); reject(err); },
    );
  });
}

/**
 * @typedef {Object} RetryOptions
 * @property {number} [attempts=3]   Total attempts including the first.
 * @property {number} [baseMs=500]   Initial delay before retry #2.
 * @property {number} [factor=2]     Multiplier per attempt.
 * @property {number} [maxMs=8000]   Upper bound per delay.
 * @property {(err: unknown, attempt: number) => boolean} [shouldRetry]
 *   Predicate to decide whether to keep retrying. Defaults to `() => true`.
 */

/**
 * Retry a thunk with exponential backoff. Returns the value from the first
 * successful attempt, or throws the last error if all attempts fail.
 *
 * @template T
 * @param {() => Promise<T>} fn
 * @param {RetryOptions} [options]
 * @returns {Promise<T>}
 */
export async function retry(fn, options = {}) {
  const {
    attempts = 3,
    baseMs = 500,
    factor = 2,
    maxMs = 8000,
    shouldRetry = () => true,
  } = options;

  /** @type {unknown} */
  let lastErr;
  for (let attempt = 1; attempt <= attempts; attempt++) {
    try {
      return await fn();
    } catch (err) {
      lastErr = err;
      if (attempt === attempts || !shouldRetry(err, attempt)) break;
      const delay = Math.min(maxMs, baseMs * factor ** (attempt - 1));
      await sleep(delay);
    }
  }
  throw lastErr;
}

/**
 * @param {number} ms
 * @returns {Promise<void>}
 */
export function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}
